import type { FSRSParameters } from 'ts-fsrs'
import { supabase } from './supabase'
import {
  getCuratedCollection as loadCuratedCollection,
  getAllCuratedCollections as loadAllCuratedCollections
} from '@/content/curated-collections'
import {
  getCollectionType,
  getCuratedSlug,
  getVirtualType,
  type CollectionId
} from '@/types/collections'
import type {
  GeneratedStudyCard,
  SentenceMineInput,
  StudyCard,
  StudyCardInfo,
  StudyDeck,
  StudyDeckDisplay,
  StudyDeckItem,
  StudyDeckWithCounts,
  StudyDefaultDeckKind,
  StudyImportPreviewRow,
  StudyNoteInput,
  StudyNoteRecord,
  StudySessionCard,
  StudyVirtualDeckId
} from '@/types/study'
import {
  buildStudyDedupeKey,
  generateStudyCards,
  getStringField,
  normalizeTemplateKeys,
  parseStudyCsv,
  studyItemsToAnkiTsv,
  studyItemsToCsv,
  validateStudyNoteInput
} from './study-card-generation'
import { mergeStudyNoteInput } from './study-note-merge'

export const DEFAULT_STUDY_LIMITS = {
  new_cards_limit: 20,
  review_limit: 200,
  rollover_hour: 4,
  desired_retention: 0.9
}

export type StudySessionCardsResult = {
  cards: StudySessionCard[]
  reviews: StudySessionCard[]
  newCards: StudySessionCard[]
  newCountToday: number
  newAllowed: number
  newLimit: number
  reviewLimit: number
  rolloverHour: number
  fsrsParams: FSRSParameters | null
}

type StudySessionOptions = {
  deck?: StudyDeck | null
  curatedSlug?: string | null
  timeZone?: string
  now?: Date
}

type CuratedEntryRow = {
  dict_entry_id: number
  entry: CuratedEntryRelation | CuratedEntryRelation[] | null
}

type CuratedEntryRelation = {
  id: number
  simplified: string | null
  traditional: string | null
  pinyin_display: string | null
  zhuyin_display: string | null
  entry_translations?: { definitions?: string[] | null }[] | null
}

type StudyCardWithNoteRow = StudyCard & {
  note?: {
    id: string
    note_type: StudyDeckItem['note_type']
    dict_entry_id: number | null
    fields: Record<string, unknown> | null
    source: Record<string, unknown> | null
    tags?: string[] | null
  } | null
}

type DeckMembershipRow = {
  deck_id: string
}

type WordNoteMembershipRow = {
  dict_entry_id: number | null
}

type StudyNoteMembershipRow = {
  card?:
    | {
        note?:
          | { dedupe_key: string | null }
          | { dedupe_key: string | null }[]
          | null
      }
    | {
        note?:
          | { dedupe_key: string | null }
          | { dedupe_key: string | null }[]
          | null
      }[]
    | null
}

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  return Array.isArray(value) ? (value[0] ?? null) : (value ?? null)
}

export function getVirtualDeckName(type: StudyVirtualDeckId): string {
  switch (type) {
    case 'all':
      return 'All Cards'
    case 'due_today':
      return 'Due Today'
    case 'review':
      return 'Review'
  }
}

export function getVirtualDeckDescription(type: StudyVirtualDeckId): string {
  switch (type) {
    case 'all':
      return 'All your study cards'
    case 'due_today':
      return 'Cards ready for review now'
    case 'review':
      return 'Cards in the review loop'
  }
}

export async function ensureDefaultStudyDeck(
  kind: StudyDefaultDeckKind
): Promise<StudyDeck> {
  const { data, error } = await supabase.rpc('ensure_default_study_deck', {
    _kind: kind
  })

  if (error) throw error
  return data as StudyDeck
}

async function getExistingDefaultStudyDeck(
  kind: StudyDefaultDeckKind
): Promise<StudyDeck | null> {
  const { data, error } = await supabase
    .from('study_decks')
    .select('*')
    .eq('default_kind', kind)
    .maybeSingle()

  if (error) throw error
  return (data as StudyDeck | null) ?? null
}

async function getAuthenticatedUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser()
  if (error) throw error
  if (!data.user?.id) throw new Error('User not logged in')
  return data.user.id
}

export async function getUserStudyDecks(): Promise<StudyDeckWithCounts[]> {
  const { data, error } = await supabase.rpc('get_study_decks_with_counts')

  if (error) throw error
  return (data || []) as StudyDeckWithCounts[]
}

export async function createStudyDeck(
  userId: string,
  name: string,
  description?: string
): Promise<StudyDeck> {
  const { data, error } = await supabase
    .from('study_decks')
    .insert({
      user_id: userId,
      name,
      description: description || null,
      is_default: false,
      desired_retention: DEFAULT_STUDY_LIMITS.desired_retention,
      new_cards_limit: DEFAULT_STUDY_LIMITS.new_cards_limit,
      review_limit: DEFAULT_STUDY_LIMITS.review_limit,
      rollover_hour: DEFAULT_STUDY_LIMITS.rollover_hour
    })
    .select()
    .single()

  if (error) throw error
  return data as StudyDeck
}

export async function updateStudyDeck(
  userId: string,
  deckId: string,
  updates: { name?: string; description?: string }
): Promise<StudyDeck> {
  const { data, error } = await supabase
    .from('study_decks')
    .update(updates)
    .eq('id', deckId)
    .eq('user_id', userId)
    .select()
    .single()

  if (error) throw error
  return data as StudyDeck
}

export async function deleteStudyDeck(
  userId: string,
  deckId: string
): Promise<void> {
  const { data: deck, error: findError } = await supabase
    .from('study_decks')
    .select('is_default')
    .eq('id', deckId)
    .eq('user_id', userId)
    .single()

  if (findError) throw findError
  if (deck?.is_default) {
    throw new Error('Default decks cannot be deleted')
  }

  const { error } = await supabase
    .from('study_decks')
    .delete()
    .eq('id', deckId)
    .eq('user_id', userId)

  if (error) throw error
}

export async function duplicateStudyDeck(
  userId: string,
  deckId: string
): Promise<StudyDeck> {
  const source = await getStudyDeckRecord(deckId, userId)
  if (!source) throw new Error('Deck not found')

  const copy = await createStudyDeck(
    userId,
    `${source.name} copy ${new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })}`,
    source.description ?? undefined
  )

  const { data: rows, error: rowsError } = await supabase
    .from('study_deck_cards')
    .select('card_id')
    .eq('deck_id', deckId)
    .eq('user_id', userId)

  if (rowsError) throw rowsError

  const inserts = (rows || []).map((row) => ({
    deck_id: copy.id,
    card_id: row.card_id,
    user_id: userId
  }))

  if (inserts.length) {
    const { error } = await supabase.from('study_deck_cards').insert(inserts)
    if (error) throw error
  }

  return copy
}

export async function getStudyNoteForEditor(
  noteId: string,
  userId: string
): Promise<StudyNoteRecord | null> {
  const { data, error } = await supabase
    .from('study_notes')
    .select('*, cards:study_cards(*)')
    .eq('id', noteId)
    .eq('user_id', userId)
    .maybeSingle()

  if (error) throw error
  return (data as StudyNoteRecord | null) ?? null
}

async function upsertGeneratedCards(
  userId: string,
  noteId: string,
  cards: GeneratedStudyCard[]
): Promise<StudyCard[]> {
  if (!cards.length) return []

  const desiredTemplates = cards.map((card) => card.template_key)
  const { error: deleteError } = await supabase
    .from('study_cards')
    .delete()
    .eq('note_id', noteId)
    .eq('user_id', userId)
    .not('template_key', 'in', `(${desiredTemplates.join(',')})`)

  if (deleteError) throw deleteError

  const { data, error } = await supabase
    .from('study_cards')
    .upsert(
      cards.map((card) => ({
        user_id: userId,
        note_id: noteId,
        template_key: card.template_key,
        front: card.front,
        back: card.back,
        card_data: card.card_data
      })),
      { onConflict: 'note_id,template_key' }
    )
    .select()

  if (error) throw error
  return (data || []) as StudyCard[]
}

async function attachCardsToDeck(
  deckId: string,
  userId: string,
  cards: StudyCard[]
): Promise<void> {
  if (!cards.length) return
  const { error } = await supabase.from('study_deck_cards').upsert(
    cards.map((card) => ({
      deck_id: deckId,
      card_id: card.id,
      user_id: userId
    })),
    { onConflict: 'deck_id,card_id' }
  )
  if (error) throw error
}

async function findExistingStudyNote(
  userId: string,
  input: StudyNoteInput,
  dedupeKey: string
): Promise<StudyNoteRecord | null> {
  const { data, error } = await supabase
    .from('study_notes')
    .select('*')
    .eq('user_id', userId)
    .eq('note_type', input.noteType)
    .eq('dedupe_key', dedupeKey)
    .maybeSingle()

  if (error) throw error
  if (data) return data as StudyNoteRecord

  const sentenceText = getStringField(input.fields, 'text')
  if (input.noteType !== 'sentence' || !sentenceText) return null

  const { data: sentenceMatch, error: sentenceError } = await supabase
    .from('study_notes')
    .select('*')
    .eq('user_id', userId)
    .eq('note_type', 'sentence')
    .filter('fields->>text', 'eq', sentenceText)
    .limit(1)
    .maybeSingle()

  if (sentenceError) throw sentenceError
  return (sentenceMatch as StudyNoteRecord | null) ?? null
}

export async function saveStudyNoteToDeck(
  userId: string,
  deckId: string,
  input: StudyNoteInput & { id?: string }
): Promise<StudyNoteRecord> {
  const templateKeys = normalizeTemplateKeys(input.noteType, input.templateKeys)
  const fields = { ...input.fields, enabled_templates: templateKeys }
  let noteInput: StudyNoteInput = { ...input, fields, templateKeys }
  const errors = validateStudyNoteInput(noteInput)
  if (errors.length) throw new Error(errors[0])
  const dedupeKey = buildStudyDedupeKey(noteInput)

  let note: StudyNoteRecord
  if (input.id) {
    const { data, error } = await supabase
      .from('study_notes')
      .update({
        note_type: input.noteType,
        dict_entry_id: input.dictEntryId ?? null,
        fields,
        source: input.source || {},
        tags: input.tags || [],
        dedupe_key: dedupeKey
      })
      .eq('id', input.id)
      .eq('user_id', userId)
      .select()
      .single()
    if (error) throw error
    note = data as StudyNoteRecord
  } else {
    const existing = await findExistingStudyNote(userId, noteInput, dedupeKey)

    if (existing) {
      noteInput = mergeStudyNoteInput(existing as StudyNoteRecord, noteInput)
      const { data, error } = await supabase
        .from('study_notes')
        .update({
          note_type: input.noteType,
          dict_entry_id: input.dictEntryId ?? null,
          fields: noteInput.fields,
          source: noteInput.source || {},
          tags: noteInput.tags || [],
          dedupe_key: dedupeKey
        })
        .eq('id', (existing as StudyNoteRecord).id)
        .eq('user_id', userId)
        .select()
        .single()
      if (error) throw error
      note = data as StudyNoteRecord
    } else {
      const { data, error } = await supabase
        .from('study_notes')
        .insert({
          user_id: userId,
          note_type: input.noteType,
          dedupe_key: dedupeKey,
          dict_entry_id: input.dictEntryId ?? null,
          fields,
          source: input.source || {},
          tags: input.tags || []
        })
        .select()
        .single()
      if (error) throw error
      note = data as StudyNoteRecord
    }
  }

  const cards = await upsertGeneratedCards(
    userId,
    note.id,
    generateStudyCards(noteInput)
  )
  await attachCardsToDeck(deckId, userId, cards)
  return { ...note, cards }
}

export async function deleteStudyNoteFromDeck(
  deckId: string,
  noteId: string,
  userId: string
): Promise<void> {
  const { data: cards, error: cardsError } = await supabase
    .from('study_cards')
    .select('id')
    .eq('note_id', noteId)
    .eq('user_id', userId)

  if (cardsError) throw cardsError
  const cardIds = (cards || []).map((card) => card.id)
  if (!cardIds.length) return

  const { error } = await supabase
    .from('study_deck_cards')
    .delete()
    .eq('deck_id', deckId)
    .eq('user_id', userId)
    .in('card_id', cardIds)
  if (error) throw error
}

export async function importStudyCsvToDeck(
  userId: string,
  deckId: string,
  csv: string
): Promise<{ preview: StudyImportPreviewRow[]; imported: number }> {
  const preview = parseStudyCsv(csv)
  const valid = preview.filter((row) => row.input && row.errors.length === 0)
  for (const row of valid) {
    await saveStudyNoteToDeck(userId, deckId, row.input!)
  }
  return { preview, imported: valid.length }
}

export async function getAllStudyDeckExportItems(
  deckId: string,
  userId: string
): Promise<StudyDeckItem[]> {
  const { data, error } = await supabase
    .from('study_deck_cards')
    .select(
      'deck_id, added_at, card:study_cards(*, note:study_notes(id, note_type, dict_entry_id, fields, source, tags))'
    )
    .eq('deck_id', deckId)
    .eq('user_id', userId)
    .order('added_at', { ascending: true })

  if (error) throw error

  return (
    (data || []) as unknown as Array<{
      deck_id: string
      card: StudyCardWithNoteRow | StudyCardWithNoteRow[] | null
    }>
  ).flatMap((row) => {
    const card = Array.isArray(row.card) ? row.card[0] : row.card
    return card ? [cardToDeckItem(card, row.deck_id)] : []
  })
}

export async function exportStudyDeckCsv(deckId: string, userId: string) {
  return studyItemsToCsv(await getAllStudyDeckExportItems(deckId, userId))
}

export async function exportStudyDeckAnkiTsv(deckId: string, userId: string) {
  return studyItemsToAnkiTsv(await getAllStudyDeckExportItems(deckId, userId))
}

export async function getStudyDeckById(
  id: CollectionId,
  userId: string | null
): Promise<StudyDeckDisplay | null> {
  const type = getCollectionType(id)

  if (type === 'user') {
    if (!userId) return null
    const { data, error } = await supabase
      .from('study_decks')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single()

    if (error || !data) return null

    const { count } = await supabase
      .from('study_deck_cards')
      .select('card_id', { count: 'exact', head: true })
      .eq('deck_id', id)
      .eq('user_id', userId)

    const deck = data as StudyDeck
    return {
      type: 'user',
      id: deck.id,
      name: deck.name,
      description: deck.description,
      item_count: count || 0,
      is_default: deck.is_default,
      default_kind: deck.default_kind,
      created_at: deck.created_at,
      updated_at: deck.updated_at
    }
  }

  if (type === 'curated') {
    const slug = getCuratedSlug(id)
    if (!slug) return null
    const curated = loadCuratedCollection(slug)
    if (!curated) return null
    return {
      type: 'curated',
      id,
      slug,
      name: curated.title,
      description: curated.description ?? null,
      item_count: curated.item_count
    }
  }

  if (type === 'virtual') {
    const virtualType = getVirtualType(id)
    if (!virtualType || !userId) return null
    const count = await getVirtualStudyCardCount(virtualType)
    return {
      type: 'virtual',
      id,
      name: getVirtualDeckName(virtualType),
      description: getVirtualDeckDescription(virtualType),
      item_count: count,
      virtualListType: virtualType
    }
  }

  return null
}

export async function getAllStudyDecks(
  userId: string | null
): Promise<StudyDeckDisplay[]> {
  const decks: StudyDeckDisplay[] = []

  if (userId) {
    const [savedWords, sentenceMining] = await Promise.all([
      ensureDefaultStudyDeck('saved_words'),
      ensureDefaultStudyDeck('sentence_mining')
    ])
    void savedWords
    void sentenceMining

    const userDecks = await getUserStudyDecks()
    decks.push(
      ...userDecks.map((deck) => ({
        type: 'user' as const,
        id: deck.id,
        name: deck.name,
        description: deck.description,
        item_count: deck.item_count,
        is_default: deck.is_default,
        default_kind: deck.default_kind,
        created_at: deck.created_at,
        updated_at: deck.updated_at,
        total_cards: deck.total_cards,
        due_cards: deck.due_cards,
        new_cards: deck.new_cards
      }))
    )

    const [allCount, dueCount, reviewCount] = await Promise.all([
      getVirtualStudyCardCount('all'),
      getVirtualStudyCardCount('due_today'),
      getVirtualStudyCardCount('review')
    ])

    decks.push(
      {
        id: 'all',
        type: 'virtual',
        name: getVirtualDeckName('all'),
        description: getVirtualDeckDescription('all'),
        item_count: allCount,
        virtualListType: 'all'
      },
      {
        id: 'due_today',
        type: 'virtual',
        name: getVirtualDeckName('due_today'),
        description: getVirtualDeckDescription('due_today'),
        item_count: dueCount,
        virtualListType: 'due_today'
      },
      {
        id: 'review',
        type: 'virtual',
        name: getVirtualDeckName('review'),
        description: getVirtualDeckDescription('review'),
        item_count: reviewCount,
        virtualListType: 'review'
      }
    )
  }

  decks.push(
    ...loadAllCuratedCollections().map((collection) => ({
      type: 'curated' as const,
      id: collection.slug,
      slug: collection.slug,
      name: collection.title,
      description: collection.description ?? null,
      item_count: collection.item_count
    }))
  )

  return decks
}

export type RecentDefaultStudyItems = {
  savedWords: StudyDeckItem[]
  sentenceMining: StudyDeckItem[]
}

type DefaultStudyDeckIdRow = {
  id: string
  default_kind: StudyDefaultDeckKind | null
}

export async function getRecentDefaultStudyItems({
  userId,
  savedWordsLimit,
  sentenceMiningLimit
}: {
  userId: string | null
  savedWordsLimit: number
  sentenceMiningLimit: number
}): Promise<RecentDefaultStudyItems> {
  if (!userId) {
    return {
      savedWords: [],
      sentenceMining: []
    }
  }

  const { data: defaultDecks, error } = await supabase
    .from('study_decks')
    .select('id, default_kind')
    .eq('user_id', userId)
    .in('default_kind', ['saved_words', 'sentence_mining'])

  if (error) throw error

  const defaultDeckRows = (defaultDecks ?? []) as DefaultStudyDeckIdRow[]
  const existingSavedWordsDeckId =
    defaultDeckRows.find((deck) => deck.default_kind === 'saved_words')?.id ??
    null
  const existingSentenceMiningDeckId =
    defaultDeckRows.find((deck) => deck.default_kind === 'sentence_mining')
      ?.id ?? null

  const [savedWordsDeck, sentenceMiningDeck] = await Promise.all([
    existingSavedWordsDeckId
      ? Promise.resolve({ id: existingSavedWordsDeckId })
      : ensureDefaultStudyDeck('saved_words'),
    existingSentenceMiningDeckId
      ? Promise.resolve({ id: existingSentenceMiningDeckId })
      : ensureDefaultStudyDeck('sentence_mining')
  ])

  const [savedWords, sentenceMining] = await Promise.all([
    getStudyDeckItems(savedWordsDeck.id, userId, 0, savedWordsLimit),
    getStudyDeckItems(sentenceMiningDeck.id, userId, 0, sentenceMiningLimit)
  ])

  return {
    savedWords,
    sentenceMining
  }
}

async function getVirtualStudyCardCount(
  type: StudyVirtualDeckId
): Promise<number> {
  let query = supabase
    .from('study_cards')
    .select('id', { count: 'exact', head: true })

  if (type === 'due_today') {
    query = query.neq('state', 0).lte('due', new Date().toISOString())
  } else if (type === 'review') {
    query = query.eq('state', 2)
  }

  const { count, error } = await query
  if (error) throw error
  return count || 0
}

export async function getStudyDeckItems(
  deckId: CollectionId,
  userId: string,
  page = 0,
  limit = 50
): Promise<StudyDeckItem[]> {
  const type = getCollectionType(deckId)
  if (type === 'user') {
    const { data, error } = await supabase.rpc('get_study_deck_items', {
      _deck_id: deckId,
      _limit: limit,
      _offset: page * limit
    })
    if (error) throw error
    return (data || []) as StudyDeckItem[]
  }

  if (type === 'curated') {
    const slug = getCuratedSlug(deckId)
    if (!slug) return []
    const offset = page * limit
    const { data, error } = await supabase
      .from('curated_entries')
      .select(
        'dict_entry_id, entry:dict_entries(id, simplified, traditional, pinyin_display, zhuyin_display, entry_translations!left(locale, definitions))'
      )
      .eq('curated_slug', slug)
      .eq('entry.entry_translations.locale', 'en')
      .order('dict_entry_id', { ascending: true })
      .range(offset, offset + limit - 1)
    if (error) throw error

    return ((data || []) as unknown as CuratedEntryRow[]).map((row) => {
      const entry = Array.isArray(row.entry) ? row.entry[0] : row.entry
      const translations = entry?.entry_translations as
        | { definitions?: string[] }[]
        | null
      const definitions = translations?.[0]?.definitions || []
      const fields = {
        simplified: entry?.simplified || '',
        traditional: entry?.traditional || entry?.simplified || '',
        pinyin: entry?.pinyin_display || '',
        definitions
      }
      return {
        card_id: `curated-${slug}-${row.dict_entry_id}`,
        deck_id: null,
        note_id: `curated-${slug}-${row.dict_entry_id}`,
        note_type: 'word',
        template_key: 'recognition',
        front:
          fields.traditional && fields.traditional !== fields.simplified
            ? `${fields.simplified} (${fields.traditional})`
            : fields.simplified,
        back: [fields.pinyin, definitions[0]].filter(Boolean).join('\n'),
        fields,
        source: { curated_slug: slug },
        dict_entry_id: row.dict_entry_id,
        state: 0,
        due: new Date().toISOString(),
        reps: 0,
        lapses: 0,
        last_review: null,
        added_at: null
      } satisfies StudyDeckItem
    })
  }

  if (type === 'virtual') {
    const virtualType = getVirtualType(deckId)
    if (!virtualType) return []
    let query = supabase
      .from('study_cards')
      .select(
        '*, note:study_notes(id, note_type, dict_entry_id, fields, source, tags)'
      )
      .order(virtualType === 'all' ? 'created_at' : 'due', { ascending: true })
      .range(page * limit, page * limit + limit - 1)

    if (virtualType === 'due_today') {
      query = query.neq('state', 0).lte('due', new Date().toISOString())
    } else if (virtualType === 'review') {
      query = query.eq('state', 2)
    }

    const { data, error } = await query
    if (error) throw error
    return ((data || []) as StudyCardWithNoteRow[]).map((card) =>
      cardToDeckItem(card, null)
    )
  }

  return []
}

export async function saveWordToDefaultDeck(
  entryId: number,
  locale = 'en'
): Promise<string> {
  const { data, error } = await supabase.rpc(
    'save_word_to_default_study_deck',
    {
      _dict_entry_id: entryId,
      _locale: locale
    }
  )
  if (error) throw error
  return data as string
}

export async function saveWordToDeck(
  deckId: string,
  entryId: number,
  locale = 'en'
): Promise<string> {
  const { data, error } = await supabase.rpc('save_word_to_study_deck', {
    _deck_id: deckId,
    _dict_entry_id: entryId,
    _locale: locale
  })
  if (error) throw error
  return data as string
}

export async function removeWordFromDeck(
  deckId: string,
  entryId: number
): Promise<void> {
  const { error } = await supabase.rpc('remove_word_from_study_deck', {
    _deck_id: deckId,
    _dict_entry_id: entryId
  })
  if (error) throw error
}

export async function removeCardFromDeck(
  deckId: string,
  cardId: string
): Promise<void> {
  const { error } = await supabase
    .from('study_deck_cards')
    .delete()
    .eq('deck_id', deckId)
    .eq('card_id', cardId)
  if (error) throw error
}

export async function removeWordFromDefaultDeck(
  entryId: number
): Promise<void> {
  const deck = await ensureDefaultStudyDeck('saved_words')
  await removeWordFromDeck(deck.id, entryId)
}

export async function isWordSaved(entryId: number): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_word_in_default_study_deck', {
    _dict_entry_id: entryId
  })
  if (error) throw error
  return data === true
}

export async function areWordsSaved(
  entryIds: number[]
): Promise<Map<number, boolean>> {
  if (!entryIds.length) return new Map()
  const { data, error } = await supabase.rpc(
    'are_words_in_default_study_deck',
    {
      _dict_entry_ids: entryIds
    }
  )
  if (error) throw error

  const map = new Map<number, boolean>()
  data?.forEach((row: { dict_entry_id: number; is_saved: boolean }) => {
    map.set(row.dict_entry_id, row.is_saved)
  })
  entryIds.forEach((id) => {
    if (!map.has(id)) map.set(id, false)
  })
  return map
}

export async function isWordInDeck(
  deckId: string,
  entryId: number
): Promise<boolean> {
  const { data, error } = await supabase
    .from('study_deck_cards')
    .select('card:study_cards!inner(note:study_notes!inner(dict_entry_id))')
    .eq('deck_id', deckId)
    .eq('card.note.dict_entry_id', entryId)
    .limit(1)

  if (error) throw error
  return Boolean(data?.length)
}

export async function getWordDeckMembership(
  entryId: number,
  deckIds: string[]
): Promise<Map<string, boolean>> {
  if (!deckIds.length) return new Map()
  const { data, error } = await supabase
    .from('study_deck_cards')
    .select(
      'deck_id, card:study_cards!inner(note:study_notes!inner(dict_entry_id))'
    )
    .in('deck_id', deckIds)
    .eq('card.note.dict_entry_id', entryId)

  if (error) throw error
  const found = new Set(
    ((data || []) as DeckMembershipRow[]).map((row) => row.deck_id)
  )
  return new Map(deckIds.map((id) => [id, found.has(id)]))
}

export async function areWordsInAnyDeck(
  entryIds: number[]
): Promise<Map<number, boolean>> {
  if (!entryIds.length) return new Map()
  const { data, error } = await supabase
    .from('study_notes')
    .select(
      'dict_entry_id, card:study_cards!inner(id, deck_cards:study_deck_cards!inner(card_id))'
    )
    .eq('note_type', 'word')
    .in('dict_entry_id', entryIds)

  if (error) throw error
  const found = new Set(
    ((data || []) as WordNoteMembershipRow[])
      .map((row) => row.dict_entry_id)
      .filter((id): id is number => typeof id === 'number')
  )
  return new Map(entryIds.map((id) => [id, found.has(id)]))
}

export async function saveSentenceToDefaultDeck(
  input: SentenceMineInput
): Promise<string> {
  const userId = await getAuthenticatedUserId()
  const deck = await ensureDefaultStudyDeck('sentence_mining')
  const note = await saveStudyNoteToDeck(
    userId,
    deck.id,
    sentenceMineToStudyNoteInput(input)
  )
  return note.cards?.[0]?.id ?? note.id
}

export async function saveSentenceToDeck(
  deckId: string,
  input: SentenceMineInput
): Promise<string> {
  const userId = await getAuthenticatedUserId()
  const note = await saveStudyNoteToDeck(
    userId,
    deckId,
    sentenceMineToStudyNoteInput(input)
  )
  return note.cards?.[0]?.id ?? note.id
}

function sentenceMineToStudyNoteInput(
  input: SentenceMineInput
): StudyNoteInput {
  return {
    noteType: 'sentence',
    fields: {
      text: input.text,
      translation: input.translation ?? null,
      source_title: input.sourceTitle
    },
    source: {
      sourceType: input.sourceType,
      source_type: input.sourceType,
      contentId: input.contentId,
      content_id: input.contentId,
      blockId: input.blockId,
      block_id: input.blockId,
      sentenceId: input.sentenceId ?? input.blockId,
      sentence_id: input.sentenceId ?? input.blockId,
      title: input.sourceTitle,
      readerHref: input.readerHref ?? null,
      reader_href: input.readerHref ?? null,
      grammarConceptIds: input.grammarConceptIds ?? [],
      grammar_concept_ids: input.grammarConceptIds ?? [],
      ...(input.source ?? {})
    },
    tags: Array.from(
      new Set(
        [
          input.sourceType,
          ...(input.tags ?? []),
          ...(input.grammarConceptIds ?? []).map((id) => `grammar:${id}`)
        ].filter(Boolean)
      )
    )
  }
}

export async function areStudyNotesInDefaultDeck(
  noteType: StudyNoteInput['noteType'],
  dedupeKeys: string[],
  defaultKind: StudyDefaultDeckKind
): Promise<Map<string, boolean>> {
  const uniqueKeys = Array.from(new Set(dedupeKeys.filter(Boolean)))
  if (!uniqueKeys.length) return new Map()

  const deck = await getExistingDefaultStudyDeck(defaultKind)
  if (!deck) return new Map(uniqueKeys.map((key) => [key, false]))

  const { data, error } = await supabase
    .from('study_deck_cards')
    .select(
      'card:study_cards!inner(note:study_notes!inner(dedupe_key,note_type))'
    )
    .eq('deck_id', deck.id)
    .eq('user_id', deck.user_id)
    .eq('card.note.note_type', noteType)
    .in('card.note.dedupe_key', uniqueKeys)

  if (error) throw error

  const found = new Set<string>()
  ;((data || []) as StudyNoteMembershipRow[]).forEach((row) => {
    const card = firstRelation(row.card)
    const note = firstRelation(card?.note)
    if (note?.dedupe_key) found.add(note.dedupe_key)
  })

  return new Map(uniqueKeys.map((key) => [key, found.has(key)]))
}

export async function areSentencesSaved(
  sentenceTexts: string[]
): Promise<Map<string, boolean>> {
  const keysByText = new Map(
    sentenceTexts.map((text) => [
      text,
      buildStudyDedupeKey({ noteType: 'sentence', fields: { text } })
    ])
  )
  const savedByKey = await areStudyNotesInDefaultDeck(
    'sentence',
    Array.from(keysByText.values()),
    'sentence_mining'
  )
  return new Map(
    Array.from(keysByText.entries()).map(([text, key]) => [
      text,
      savedByKey.get(key) === true
    ])
  )
}

export async function isSentenceSaved(sentenceText: string): Promise<boolean> {
  return (await areSentencesSaved([sentenceText])).get(sentenceText) === true
}

export async function getStudySessionCards(
  collectionId: CollectionId,
  userId: string,
  options: StudySessionOptions = {}
): Promise<StudySessionCardsResult> {
  const type = getCollectionType(collectionId)
  const now = options.now ?? new Date()
  const timeZone = options.timeZone ?? 'UTC'
  let deckId: string | null = null
  let curatedSlug = options.curatedSlug ?? null
  let virtualType: StudyVirtualDeckId | null = null
  let newLimit = DEFAULT_STUDY_LIMITS.new_cards_limit
  let reviewLimit = DEFAULT_STUDY_LIMITS.review_limit
  let rolloverHour = DEFAULT_STUDY_LIMITS.rollover_hour
  let fsrsParams: FSRSParameters | null = null

  if (type === 'user') {
    deckId = collectionId
    const deck =
      options.deck ?? (await getStudyDeckRecord(collectionId, userId))
    newLimit = deck?.new_cards_limit ?? newLimit
    reviewLimit = deck?.review_limit ?? reviewLimit
    rolloverHour = deck?.rollover_hour ?? rolloverHour
    fsrsParams = deck?.fsrs_params ?? null
  } else if (type === 'curated') {
    curatedSlug = curatedSlug ?? getCuratedSlug(collectionId)
  } else if (type === 'virtual') {
    virtualType = getVirtualType(collectionId)
  }

  const { data, error } = await supabase.rpc('get_study_session_cards', {
    _deck_id: deckId,
    _virtual_type: virtualType,
    _curated_slug: curatedSlug,
    _now: now.toISOString(),
    _time_zone: timeZone,
    _review_limit: reviewLimit,
    _new_limit: newLimit,
    _rollover_hour: rolloverHour,
    _locale: 'en'
  })
  if (error) throw error

  const payload = (data || {}) as {
    reviews?: StudySessionCard[]
    new_cards?: StudySessionCard[]
    new_count_today?: number
    new_allowed?: number
    new_limit?: number
    review_limit?: number
    rollover_hour?: number
  }
  const reviews = payload.reviews || []
  const newCards = payload.new_cards || []

  return {
    cards: [...reviews, ...newCards],
    reviews,
    newCards,
    newCountToday: payload.new_count_today ?? 0,
    newAllowed: payload.new_allowed ?? 0,
    newLimit: payload.new_limit ?? newLimit,
    reviewLimit: payload.review_limit ?? reviewLimit,
    rolloverHour: payload.rollover_hour ?? rolloverHour,
    fsrsParams
  }
}

async function getStudyDeckRecord(
  deckId: string,
  userId: string
): Promise<StudyDeck | null> {
  const { data, error } = await supabase
    .from('study_decks')
    .select('*')
    .eq('id', deckId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return (data as StudyDeck | null) ?? null
}

export async function submitStudyReview(input: {
  cardId: string
  rating: number
  deckId?: string | null
  curatedSlug?: string | null
  durationMs?: number
}): Promise<{ card: StudySessionCard }> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Not authenticated')

  const response = await fetch('/api/study/review', {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to submit review')
  }
  return payload as { card: StudySessionCard }
}

function cardToDeckItem(
  card: StudyCardWithNoteRow,
  deckId: string | null
): StudyDeckItem {
  return {
    card_id: card.id,
    deck_id: deckId,
    note_id: card.note_id,
    note_type: card.note?.note_type || 'custom',
    template_key: card.template_key || 'recognition',
    front: card.front,
    back: card.back,
    fields: { ...(card.note?.fields || {}), tags: card.note?.tags || [] },
    source: card.note?.source || {},
    dict_entry_id: card.note?.dict_entry_id ?? null,
    state: card.state,
    due: card.due,
    reps: card.reps,
    lapses: card.lapses,
    last_review: card.last_review,
    added_at: card.created_at
  }
}

export function cardInfoFromDeckItem(item: StudyDeckItem): StudyCardInfo {
  return {
    card_id: item.card_id,
    note_type: item.note_type,
    dict_entry_id: item.dict_entry_id,
    due: item.due,
    last_review: item.last_review,
    reps: item.reps,
    lapses: item.lapses,
    state: item.state,
    front: item.front,
    back: item.back
  }
}
